/// <amd-module name="Wit/WorkItemDataManager/Sources/WorkItemAsyncDataProviderSource" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";
import { QueryErrorPolicy, WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { WITCommonConstants } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import {
    FieldRuleType,
    IFieldProjectData,
    ILinkTypes,
    IRouteParams,
    IUnparsedWorkItemTypeData,
    IWorkItemData,
    IWorkItemDataSource,
    IWorkItemTypeData
} from "Wit/WorkItemDataManager/Contracts";
import { canDeserializeRules } from "Wit/WorkItemDataManager/Utils";
import { WorkItemMetadataCacheStampManager } from "../CacheManagers/WorkItemMetadataCacheStampManager";
import { WorkItemStoreLevelWITDataSource } from "./WorkItemStoreLevelWITDataSource";

export class WorkItemAsyncDataProviderSource implements IWorkItemDataSource {
    private workItemStoreSource: WorkItemStoreLevelWITDataSource;
    private _hostId: string;
    private _cacheStampManager: WorkItemMetadataCacheStampManager;

    constructor(pageContext: IVssPageContext, cacheStampManager: WorkItemMetadataCacheStampManager) {
        this.workItemStoreSource = new WorkItemStoreLevelWITDataSource(pageContext);
        this._cacheStampManager = cacheStampManager;
        this._hostId = pageContext.getService<IVssPageService>("IVssPageService").getData()?.hostId ?? "";
    }

    public beginGetWorkItemData(
        ids: number[],
        isDeleted?: boolean,
        includeInRecentActivity?: boolean,
        includeHistory?: boolean,
        errorPolicy?: QueryErrorPolicy
    ): Promise<IWorkItemData[] | null> {
        const apiName = !includeHistory ? "workitemsnohistory" : "workitems";
        const routeParams: IRouteParams = { controller: "wit", action: apiName };

        return this.workItemStoreSource
            .getDataWithoutCache<IWorkItemData[]>(routeParams, {
                ids: ids,
                isDeleted: isDeleted,
                includeInRecentActivity: includeInRecentActivity,
                includeHistory: includeHistory,
                errorPolicy: errorPolicy
            })
            .then(response => {
                return response.data;
            });
    }

    public async beginGetWorkItemTypeData(projectId: string, typeNames: string[]): Promise<IWorkItemTypeData[] | null> {
        const params = await this._cacheStampManager.addStampToParams(WITCommonConstants.WorkItemTypes, { typeNames, projectId });

        const response = await this.workItemStoreSource.getDataWithoutCache<IUnparsedWorkItemTypeData[]>(
            { controller: "wit", action: "workItemTypes", routeData: { project: projectId } },
            params
        );

        const data = response.data;

        const parsedData = data.map(type => {
            const workItemTypeData: IWorkItemTypeData = { ...type, rules: { ...type.rules, fieldRules: {} } };

            if (canDeserializeRules(type)) {
                const fieldRules = deserializeVssJsonObject<{ [fieldId: number]: FieldRuleType[] }>(type.rules.fieldRules as string);
                if (fieldRules) {
                    workItemTypeData.rules.fieldRules = fieldRules;
                }
            } else {
                workItemTypeData.rules.fieldRules = type.rules.fieldRules as any as { [fieldId: number]: FieldRuleType[] };
            }

            return workItemTypeData;
        });

        return parsedData;
    }

    public async beginGetFields(): Promise<IFieldEntry[] | null> {
        const params = await this._cacheStampManager.addStampToParams(WITCommonConstants.WorkItemTypes, null);

        return (
            await this.workItemStoreSource.getDataFromCacheAsync<IFieldEntry[]>(
                { controller: "wit", action: "fields" },
                params,
                this._hostId,
                WITCommonConstants.Fields,
                "_"
            )
        ).data;
    }

    public async beginGetNodes(projectId: string): Promise<WorkItemClassificationNode[] | null> {
        const params = await this._cacheStampManager.addStampToParams(WITCommonConstants.Nodes, {projectId}, );

        return (
            await this.workItemStoreSource.getDataFromCacheAsync<WorkItemClassificationNode[]>(
                { controller: "wit", action: "nodes", routeData: { project: projectId } },
                params,
                this._hostId,
                WITCommonConstants.Nodes,
                "_"
            )
        ).data;
    }

    public async beginGetFieldProjectData(projectId: string): Promise<IFieldProjectData | null> {
        const params = await this._cacheStampManager.addStampToParams(WITCommonConstants.TeamProjects, {
            includeFieldDefinitions: true,
            namesOrIds: [projectId]
        });

        return (
            await this.workItemStoreSource.getDataFromCacheAsync<IFieldProjectData>(
                { controller: "wit", action: "teamProjects", routeData: { project: projectId } },
                params,
                this._hostId,
                WITCommonConstants.TeamProjects,
                "_"
            )
        ).data;
    }

    public async beginGetLinkTypes(): Promise<ILinkTypes | null> {
        const params = await this._cacheStampManager.addStampToParams(WITCommonConstants.LinkTypes, null);

        return (
            await this.workItemStoreSource.getDataFromCacheAsync<ILinkTypes>(
                { controller: "wit", action: "linkTypes" },
                params,
                this._hostId,
                WITCommonConstants.LinkTypes,
                "_"
            )
        ).data;
    }

    public async beginGetConstantSets(ids: string[]): Promise<{ [key: string]: string[] } | null> {
        if (ids.length === 0) {
            return Promise.resolve({});
        }

        const params = await this._cacheStampManager.addStampToParams(WITCommonConstants.ConstantSets, {
            ids
        });

        const key = this._cacheStampManager.makeCacheKey(ids);

        return (
            await this.workItemStoreSource.getDataFromCacheAsync<{ [key: string]: string[] }>(
                { controller: "wit", action: "constantSets" },
                params,
                this._hostId,
                WITCommonConstants.ConstantSets,
                key
            )
        ).data;
    }
}
